import React, { useEffect, useState } from "react";
import { Spinner, Badge, OverlayTrigger, Tooltip, Modal, Button, Table } from "react-bootstrap";
import { FiClock, FiAlertCircle, FiCheckCircle, FiInfo, FiX, FiTrendingUp, FiShoppingBag, FiList } from "react-icons/fi";

// Elenco delle festività nazionali italiane fisse (MM-DD)
const FESTIVITA_ITALIANE = [
  "01-01", // Capodanno
  "01-06", // Epifania
  "04-25", // Liberazione
  "05-01", // Festa del Lavoro
  "06-02", // Festa della Repubblica
  "08-15", // Ferragosto
  "11-01", // Tutti i Santi
  "12-08", // Immacolata
  "12-25", // Natale
  "12-26", // Santo Stefano
];

const getLunedediAngelelo = (anno) => {
  const a = anno % 19;
  const b = Math.floor(anno / 100);
  const c = anno % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mese = Math.floor((h + l - 7 * m + 114) / 31);
  const giorno = ((h + l - 7 * m + 114) % 31) + 1;
  
  const pasqua = new Date(anno, mese - 1, giorno);
  pasqua.setDate(pasqua.getDate() + 1);
  return pasqua;
};

const isFestivoOWeekend = (data) => {
  const giornoSettimana = data.getDay();
  if (giornoSettimana === 0 || giornoSettimana === 6) return true;

  const mmDd = `${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
  if (FESTIVITA_ITALIANE.includes(mmDd)) return true;

  const pasquetta = getLunedediAngelelo(data.getFullYear());
  if (data.toDateString() === pasquetta.toDateString()) return true;

  return false;
};

const calcolaGiorniLavorativiEffettivi = (dataInizio, dataFine) => {
  if (dataFine <= dataInizio) return 0;

  let giorniLavorativi = 0;
  let dataCorrente = new Date(dataInizio);
  dataCorrente.setDate(dataCorrente.getDate() + 1);

  while (dataCorrente <= dataFine) {
    if (!isFestivoOWeekend(dataCorrente)) {
      giorniLavorativi++;
    }
    dataCorrente.setDate(dataCorrente.getDate() + 1);
  }

  return giorniLavorativi;
};

const MediaFornitoriCard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFornitore, setSelectedFornitore] = useState(null);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const entrate = json?.ENTRATE || (Array.isArray(json) ? json : []);
        const perFornitore = {};
        const vistiNumDoc = new Set();
        const annoCorrente = new Date().getFullYear();

        entrate.forEach((e) => {
          const dataOrdine = new Date(e["Data Ordine"]);
          if (isNaN(dataOrdine) || dataOrdine.getFullYear() !== annoCorrente) return;

          const numDoc = e["Num Doc"];
          if (numDoc && vistiNumDoc.has(numDoc)) return;
          if (numDoc) vistiNumDoc.add(numDoc);

          const targetSpedizione = (parseInt(e["Giorni Spedizione"]) || 0) - 1;
          if (targetSpedizione < 0) return; 

          const fornitore = e["Ragione sociale"] || "N/A";
          const dataMagazzino = new Date(e["data_entrata"]);

          if (isNaN(dataMagazzino)) return;

          const diffDaysMag = calcolaGiorniLavorativiEffettivi(dataOrdine, dataMagazzino);

          if (!perFornitore[fornitore]) {
            perFornitore[fornitore] = {
              idFornitore: e["ID Fornitore"] || "N/A",
              totaleGiorniMagazzino: 0,
              countMagazzino: 0,
              inTargetMagazzino: 0,
              target: targetSpedizione,
              ordini: []
            };
          }

          let statusOrdine = "danger";
          if (diffDaysMag <= targetSpedizione) statusOrdine = "success";
          else if (diffDaysMag <= targetSpedizione + 1) statusOrdine = "warning";

          perFornitore[fornitore].ordini.push({
            idOrdine: e["ID Ordine"],
            numDoc: numDoc,
            dataOrdine: e["Data Ordine"].split(" ")[0],
            dataEntrata: e["data_entrata"].split(" ")[0],
            giorniLavorativiReali: diffDaysMag,
            statusOrdine: statusOrdine
          });

          perFornitore[fornitore].totaleGiorniMagazzino += diffDaysMag;
          perFornitore[fornitore].countMagazzino += 1;

          if (diffDaysMag <= targetSpedizione) {
            perFornitore[fornitore].inTargetMagazzino += 1;
          }
        });

        const risultato = Object.entries(perFornitore)
          .map(([fornitore, val]) => {
            const mediaMag = val.countMagazzino > 0
              ? Math.round((val.totaleGiorniMagazzino / val.countMagazzino) * 2) / 2
              : null;

            const pctMagazzino = val.countMagazzino > 0
              ? Math.round((val.inTargetMagazzino / val.countMagazzino) * 100)
              : null;

            const getPctStyle = (pct) => {
              if (pct === null) return { bg: "#f1f3f5", text: "#adb5bd" };
              if (pct > 75) return { bg: "#e6fcf5", text: "#099268" };
              if (pct >= 50) return { bg: "#fff9db", text: "#f08000" };
              return { bg: "#fff5f5", text: "#e03131" };
            };

            let statusMag = "danger";
            if (mediaMag === null) statusMag = "none";
            else if (mediaMag <= val.target) statusMag = "success";
            else if (mediaMag <= val.target + 1) statusMag = "warning";

            return {
              fornitore,
              idFornitore: val.idFornitore,
              target: val.target,
              countMagazzino: val.countMagazzino,
              inTargetMagazzino: val.inTargetMagazzino,
              mediaMagazzino: mediaMag !== null ? mediaMag.toFixed(1) + " gg" : "--",
              pctMagazzino,
              styleMag: getPctStyle(pctMagazzino),
              statusMag,
              ordini: val.ordini.sort((a, b) => b.idOrdine - a.idOrdine)
            };
          })
          .sort((a, b) => a.fornitore.localeCompare(b.fornitore));

        setData(risultato);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore nel caricamento dei dati JSON:", err);
        setLoading(false);
      });
  }, []);

  const handleOpenDettaglio = (fornitoreObj) => {
    setSelectedFornitore(fornitoreObj);
    setShowDetailModal(true);
  };

  const renderBadgeStatus = (status, mini = true) => {
    if (status === "none") return <span className="text-muted text-center d-block">--</span>;
    
    const config = {
      success: { bg: "success-subtle", text: "text-success", border: "border-success-alpha", icon: <FiCheckCircle size={mini ? 10 : 13} />, label: "In Target" },
      warning: { bg: "warning-subtle", text: "text-warning", border: "border-warning-alpha", icon: <FiClock size={mini ? 10 : 13} />, label: "Alert" },
      danger: { bg: "danger-subtle", text: "text-danger", border: "border-danger-alpha", icon: <FiAlertCircle size={mini ? 10 : 13} />, label: "Ritardo" }
    };

    const current = config[status];
    return (
      <Badge bg={current.bg} className={`${mini ? 'status-badge-mini' : 'status-badge-md'} ${current.text} border ${current.border} text-uppercase`}>
        {current.icon} {current.label}
      </Badge>
    );
  };

  const renderTooltip = (props) => (
    <Tooltip id="info-performance-tooltip" {...props}>
      <div style={{ textAlign: 'left', fontSize: '0.7rem', padding: '5px' }}>
        <strong>MONITORAGGIO PERFORMANCE:</strong><br/>
        • Mostra solo gli ordini dell'anno in corso.<br/>
        • Target calcolato come <strong>Giorni Spedizione - 1</strong>.<br/>
        • Raggruppamento e somma degli ordini per Fornitore.<br/>
        • Esclude il giorno dell'ordine, Weekend e Festività Nazionali.
      </div>
    </Tooltip>
  );

  const renderTableContent = () => (
    <table className="table table-hover mb-0" style={{ fontSize: "0.70rem" }}>
      <thead className="bg-white sticky-top" style={{ zIndex: 1, boxShadow: "0 2px 2px -1px rgba(0,0,0,0.05)" }}>
        <tr>
          <th className="border-0 text-muted ps-3" style={{ verticalAlign: "middle" }}>FORNITORE</th>
          <th className="border-0 text-muted text-center border-end" style={{ verticalAlign: "middle", width: "80px" }}>TARGET (-1gg)</th>
          <th className="border-0 text-primary text-center bg-light" style={{ fontSize: "0.65rem", fontWeight: "800", borderBottom: "2px solid #0d6efd" }}>MEDIA REALE LAVORATIVI / PERFORMANCE</th>
          <th className="border-0 text-primary text-center bg-light" style={{ fontSize: "0.65rem", fontWeight: "800", borderBottom: "2px solid #0d6efd" }}>STATUS</th>
        </tr>
      </thead>
      <tbody>
        {data.map((f, i) => (
          <tr key={i} style={{ verticalAlign: "middle", cursor: "pointer" }} onClick={() => handleOpenDettaglio(f)}>
            <td className="ps-3">
              <div className="fw-bold" style={{ lineHeight: "1.2", color: "#333", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.fornitore}
              </div>
              <div style={{ fontSize: "0.58rem", color: "#999" }}>
                ID: {f.idFornitore} • Totale Ordini: {f.countMagazzino} 
              </div>
            </td>

            <td className="text-center text-muted border-end fw-bold">{f.target} gg</td>
            
            <td className="text-center bg-light">
              <span className="fw-bold text-primary d-block" style={{ fontSize: "0.75rem" }}>{f.mediaMagazzino}</span>
              {f.pctMagazzino !== null ? (
                <div className="badge-small-pct mt-1" style={{ backgroundColor: f.styleMag.bg, color: f.styleMag.text }}>
                  {f.pctMagazzino}% 
                </div>
              ) : (
                <span className="text-muted" style={{ fontSize: "0.6rem" }}>Nessun dato</span>
              )}
            </td>
            <td className="text-center bg-light">
              {renderBadgeStatus(f.statusMag)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="ombra card tb" style={{ borderRadius: "12px", border: "1px solid #dee2e6", height: "450px", display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fff" }}>
      <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white" style={{ zIndex: 110 }}>
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span style={{ fontWeight: "800", fontSize: "0.75rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
            PERFORMANCE FORNITORI (TARGET CORRETTO -1 GG)
          </span>
          <OverlayTrigger placement="bottom" overlay={renderTooltip}>
            <span className="ms-2" onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }}><FiInfo size={14} color="#adb5bd" /></span>
          </OverlayTrigger>
        </div>
      </div>

      <div className="scroll-container" style={{ flexGrow: 1, overflowY: "auto" }}>
        <style>{`
          .scroll-container::-webkit-scrollbar { width: 4px; }
          .scroll-container::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
          .badge-small-pct { padding: 4px 6px; font-size: 0.65rem; font-weight: 800; border-radius: 4px; min-width: 42px; display: inline-block; text-align: center; }
          .status-badge-mini { padding: 4px 6px; font-size: 0.58rem; display: inline-flex; align-items: center; gap: 3px; letter-spacing: 0.5px; white-space: nowrap; }
          .status-badge-md { padding: 6px 10px; font-size: 0.7rem; display: inline-flex; align-items: center; gap: 4px; letter-spacing: 0.5px; font-weight: 700; }
          .table td { padding: 12px 8px !important; }
          
          .supplier-modal-content {
            max-width: 750px !important;
            width: 100% !important;
            margin: 0 auto !important;
            border-radius: 16px !important;
            box-shadow: 0 15px 40px rgba(0,0,0,0.12) !important;
            overflow: hidden;
          }
          .kpi-supplier-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
          .kpi-supplier-card {
            padding: 14px;
            border-radius: 12px;
            background: #fdfdfd;
            border: 1px solid #eaeaea;
          }
          .kpi-supplier-title { font-size: 0.65rem; font-weight: 700; color: #8c8c8c; text-transform: uppercase; margin-bottom: 4px; letter-spacing: 0.5px;}
          .kpi-supplier-value { font-size: 1.05rem; font-weight: 800; color: #1a1a1a; }
        `}</style>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" variant="danger" size="sm" />
          </div>
        ) : (
          renderTableContent()
        )}
      </div>

      {/* MODALE TUTTO SCHERMO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                PERFORMANCE FORNITORI DETTAGLIATA (TARGET -1 GG)
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltip}>
                <span style={{ cursor: "pointer" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="bg-white p-3 shadow-sm rounded-4 border scroll-container">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <Spinner animation="border" variant="danger" size="sm" />
                </div>
              ) : (
                renderTableContent()
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE DETTAGLIO FORNITORE - PULITA (SENZA SCROLL DOPPIO) */}
<Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered
        contentClassName="supplier-modal-content"
      >
        <Modal.Header closeButton className="px-4 pt-4 pb-2 border-0 bg-white">
          <Modal.Title className="fw-extrabold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem", color: "#0d6efd", letterSpacing: "0.3px" }}>
            <FiList size={22} /> ANALISI TRACCIABILITÀ FORNITORE
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-white px-4 pb-3" style={{ overflow: "hidden" }}> {/* Blocco totale di scroll spuri sul body */}
          
          {/* Info Strip Intestazione Fornitore */}
          <div className="p-3 mb-4 rounded-3 d-flex justify-content-between align-items-center" style={{ background: "#f8f9fa", borderLeft: "4px solid #0d6efd" }}>
            <div>
              <small className="text-muted d-block fw-bold" style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}>RAGIONE SOCIALE</small>
              <span className="fw-bold text-dark d-block" style={{ fontSize: "0.95rem" }}>{selectedFornitore?.fornitore}</span>
              <small className="text-muted" style={{ fontSize: "0.65rem" }}>ID Fornitore Interno: <strong>{selectedFornitore?.idFornitore}</strong></small>
            </div>
            <div className="text-end">
              <small className="text-muted d-block fw-bold" style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}>TARGET ATTUALE</small>
              <Badge bg="dark" className="fw-bold" style={{ fontSize: "0.75rem" }}>{selectedFornitore?.target} GIORNI</Badge>
            </div>
          </div>

          {/* Griglia KPI di sintesi */}
          <div className="kpi-supplier-grid mb-4">
            <div className="kpi-supplier-card" style={{ borderTop: "3px solid #0d6efd" }}>
              <div className="kpi-supplier-title"><FiShoppingBag className="me-1" /> Carico Ordini</div>
              <div className="kpi-supplier-value text-primary">{selectedFornitore?.countMagazzino} totali</div>
            </div>
            <div className="kpi-supplier-card" style={{ borderTop: "3px solid #198754" }}>
              <div className="kpi-supplier-title"><FiCheckCircle className="me-1 text-success" /> Ordini In Target</div>
              <div className="kpi-supplier-value text-success">{selectedFornitore?.inTargetMagazzino} evasi</div>
            </div>
            <div className="kpi-supplier-card" style={{ borderTop: "3px solid #f08000" }}>
              <div className="kpi-supplier-title"><FiTrendingUp className="me-1" /> Efficienza Media</div>
              <div className="kpi-supplier-value d-flex align-items-center gap-2">
                <span>{selectedFornitore?.mediaMagazzino}</span>
                <Badge style={{  fontSize: '0.6rem' }}>
                  {selectedFornitore?.pctMagazzino}%
                </Badge>
              </div>
            </div>
          </div>

          {/* Titolo Registro */}
          <span className="d-block fw-extrabold text-muted mb-2" style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>REGISTRO DETTAGLIATO ORDINI (ANNO CORRENTE)</span>
          
          {/* UNICO CONTENITORE DI SCROLL INTERNO */}
          <div style={{ maxHeight: "350px", overflowY: "auto", overflowX: "hidden", border: "1px solid #eaeaea", borderRadius: "6px" }}>
            {/* RIMOSSO 'responsive' da Table per evitare il conflitto di scroll di Bootstrap */}
            <Table hover className="mb-0 align-middle" style={{ fontSize: "0.7rem", width: "100%", tableLayout: "fixed" }}>
              <thead className="bg-light sticky-top" style={{ zIndex: 1, top: 0 }}>
                <tr style={{ borderBottom: "2px solid #eaeaea" }}>
                  <th className="py-2 ps-2 text-muted bg-light" style={{ width: "30%" }}>ID / DOC ORDINE</th>
                  <th className="py-2 text-muted text-center bg-light" style={{ width: "18%" }}>DATA ORDINE</th>
                  <th className="py-2 text-muted text-center bg-light" style={{ width: "18%" }}>DATA ENTRATA</th>
                  <th className="py-2 text-muted text-center bg-light" style={{ width: "18%" }}>LAVORATIVI REALI</th>
                  <th className="py-2 text-muted text-center pe-2 bg-light" style={{ width: "16%" }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {selectedFornitore?.ordini?.map((ord, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f8f9fa" }}>
                    <td className="py-2.5 ps-2 text-truncate">
                      <div className="fw-bold text-dark text-truncate">Doc: {ord.numDoc || "N/A"}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: "0.6rem" }}>ID: {ord.idOrdine}</div>
                    </td>
                    <td className="py-2.5 text-center text-secondary fw-medium">{ord.dataOrdine}</td>
                    <td className="py-2.5 text-center text-secondary fw-medium">{ord.dataEntrata}</td>
                    <td className="py-2.5 text-center text-dark fw-bold" style={{ fontSize: "0.75rem" }}>{ord.giorniLavorativiReali} gg</td>
                    <td className="py-2.5 text-center pe-2">
                      {renderBadgeStatus(ord.statusOrdine, true)}
                    </td>
                  </tr>
                ))}
                {selectedFornitore?.ordini?.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">Nessun ordine registrato.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

        </Modal.Body>
        <Modal.Footer className="bg-light border-0 justify-content-end py-2.5">
          <Button variant="secondary" size="sm" style={{ borderRadius: '6px', fontSize: '0.65rem', fontWeight: '600' }} onClick={() => setShowDetailModal(false)}>
            Chiudi Dettaglio
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
  );
};

export default MediaFornitoriCard;